import {
  Dropdown,
  DropdownOption,
  DropdownProps,
  Icon,
} from '@unmind/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import './DaysAndHoursInput.scss';

/**
 * Colour needs the styled-components theme — Sass has no token access here
 * (see MEMORY: "Sass cannot reach colour tokens"). Layout lives in
 * DaysAndHoursInput.scss.
 *
 * `cardHighlighted` is the closest theme token to the design's
 * `--color-surface-nested` — it resolves to exactly `#f8f6f1` on the Unmind
 * channel, and to Telus's own equivalent surface tint on that channel. It's
 * already used for the scale track (see ScaleInput.tsx).
 */
const Tip = styled.div`
  background: ${({ theme }) => theme.colors.background.cardHighlighted};
`;

export type DaysAndHoursValue = { days: number; hours: number };

export type DaysAndHoursInputProps = {
  assumedWorkingDaysPerWeek: number;
  assumedWorkingHoursPerDay: number;
  days: number;
  hours: number;
  onChange(next: DaysAndHoursValue): void;
};

const DAYS_INPUT_ID = 'wellbeing-measure-days';
const HOURS_INPUT_ID = 'wellbeing-measure-hours';

/**
 * Taken from the prop rather than rebuilt: the design system doesn't export
 * the value union, and deriving it keeps this in step if `Dropdown` widens it.
 */
type DropdownValue = Parameters<DropdownProps<number>['onChange']>[0];

/**
 * `Dropdown` can also be multi-select or clearable, so `onChange` may hand
 * back an array or nothing at all. This control is neither, and every option
 * is built with a number, so anything else is ignored.
 */
const toSelectedNumber = (value: DropdownValue) =>
  typeof value === 'number' ? value : null;

/**
 * The absenteeism control: two figures, submitted separately.
 *
 * Ranges come from the questionnaire's working-week attributes, never from
 * constants — hardcoding them means the UI disagrees with the server's
 * validation the moment the attributes change.
 *
 * `floatMenu` is what makes the design system `Dropdown` usable here:
 * the menu is portalled to `<body>` and positioned against the viewport,
 * so the modal card no longer clips it. It brings `closeOnScroll` with
 * it by default, which is what stops a menu following its control out
 * through the bottom of a scrolling modal.
 */
const DaysAndHoursInput = ({
  assumedWorkingDaysPerWeek,
  assumedWorkingHoursPerDay,
  days,
  hours,
  onChange,
}: DaysAndHoursInputProps) => {
  const { t: translate } = useTranslation('homepage');

  const isAtMaximumDays = days >= assumedWorkingDaysPerWeek;

  const daysLabel = translate('wellbeing_measure.absenteeism.days_label');
  const hoursLabel = translate('wellbeing_measure.absenteeism.hours_label');

  const dayOptions: DropdownOption<number>[] = Array.from(
    { length: assumedWorkingDaysPerWeek + 1 },
    (_, count) => ({
      value: count,
      label: translate('wellbeing_measure.absenteeism.days_option', {
        count,
      }),
    }),
  );

  // Hours stop one short of a full day: a whole day off is counted in days.
  const hourOptions: DropdownOption<number>[] = Array.from(
    { length: assumedWorkingHoursPerDay },
    (_, count) => ({
      value: count,
      label: translate('wellbeing_measure.absenteeism.hours_option', {
        count,
      }),
    }),
  );

  const onDaysChange = (value: DropdownValue) => {
    const selected = toSelectedNumber(value);

    if (selected === null) {
      return;
    }

    /**
     * Zeroing matters as much as disabling: a retained hours value would
     * still be submitted, and the server rejects a combined figure larger
     * than the working week.
     */
    onChange({
      days: selected,
      hours: selected >= assumedWorkingDaysPerWeek ? 0 : hours,
    });
  };

  const onHoursChange = (value: DropdownValue) => {
    const selected = toSelectedNumber(value);

    if (selected === null) {
      return;
    }

    onChange({ days, hours: selected });
  };

  return (
    <div className="wellbeing-measure-absenteeism">
      <div className="wellbeing-measure-absenteeism__inputs">
        <div className="wellbeing-measure-absenteeism__field">
          {/* Labelled from here rather than through `additionalText`: the
              design puts a `body-s` label 4px above the control, where the
              design system's own `LabelAndHelpText` renders its label type at
              a 12px gap. `id` reaches react-select's input as `inputId`, so
              `htmlFor` still names the control. */}
          <label
            className="body-s wellbeing-measure-absenteeism__label"
            htmlFor={DAYS_INPUT_ID}
          >
            {daysLabel}
          </label>
          <Dropdown
            id={DAYS_INPUT_ID}
            options={dayOptions}
            selectedValue={days}
            placeholder={daysLabel}
            onChange={onDaysChange}
            floatMenu
          />
        </div>
        <div className="wellbeing-measure-absenteeism__field">
          <label
            className="body-s wellbeing-measure-absenteeism__label"
            htmlFor={HOURS_INPUT_ID}
          >
            {hoursLabel}
          </label>
          <Dropdown
            id={HOURS_INPUT_ID}
            options={hourOptions}
            selectedValue={hours}
            placeholder={hoursLabel}
            onChange={onHoursChange}
            floatMenu
            isDisabled={isAtMaximumDays}
          />
          {/* The region stays in the DOM and only its text changes. A live
              region that appears at the same moment as its text is often
              missed by screen readers. It sits outside the dropdown because
              react-select overwrites any `aria-describedby` we pass. */}
          <div role="status" className="wellbeing-measure-absenteeism__status">
            {isAtMaximumDays && (
              <p className="body-s wellbeing-measure-absenteeism__help-text">
                {translate(
                  'wellbeing_measure.absenteeism.hours_disabled_explanation',
                )}
              </p>
            )}
          </div>
        </div>
      </div>
      <Tip className="wellbeing-measure-absenteeism__tip">
        <Icon
          icon="information"
          size="medium"
          aria-hidden="true"
          className="wellbeing-measure-absenteeism__tip-icon"
        />
        <p className="body-s wellbeing-measure-absenteeism__tip-text">
          {translate('wellbeing_measure.absenteeism.tip')}
        </p>
      </Tip>
    </div>
  );
};

export default DaysAndHoursInput;
